import {
  action, computed, makeAutoObservable, observable, observe, runInAction, when,
} from 'mobx';
import { Tier, TierColors, TierConfig } from '@gamiphy/bl/level';
import { LevelApi } from '@gamiphy/gamix-modules/level';
import { UserStore } from '@gamiphy/loyalty-station-desktop/store/user';
import { AppStore } from '@gamiphy/loyalty-station-desktop/store/app';

class LevelStore {
  @observable loading = false;

  @observable tiers: Tier[] = [];

  @observable currentTier?: Tier = undefined;

  @observable nextTier?: Tier = undefined;

  @observable progress = 0;

  @observable accumulated = 0;

  @observable tierConfig?: TierConfig = undefined;

  constructor() {
    makeAutoObservable(this);

    void this.initialize();
  }

  @computed get hasLevels(): boolean {
    return this.tiers.length > 0;
  }

  @computed get userLevelIcon(): string {
    if (UserStore.isLoggedIn) {
      return this.currentTier ? this.currentTier.icon : '';
    }

    const tier = this.tiers.find((t) => t.order === 0);
    return tier ? tier.icon : '';
  }

  @computed
  public get tierColor(): string {
    const tier = UserStore.user?.tier ?? 0;

    switch (tier) {
      case 0:
        return TierColors.bronze;
      case 1:
        return TierColors.silver;
      case 2:
        return TierColors.gold;
      case 3:
        return TierColors.platinum;
      case 4:
        return TierColors.diamond;
      default:
        return TierColors.diamond;
    }
  }

  @action
  async initialize(): Promise<void> {
    await when(() => AppStore.initialized);

    const [
      tiers,
      tierConfig,
    ] = await Promise.all([
      LevelApi.getTiers(),
      LevelApi.getTierConfig(),
    ]);

    runInAction(() => {
      this.tiers = tiers;
      this.tierConfig = tierConfig;
    });

    void this.loadUserLevel();

    observe(UserStore, 'user', () => this.loadUserLevel());
  }

  @action
  async loadUserLevel(): Promise<void> {
    await when(() => !!UserStore.user);

    if (UserStore.user) {
      const data = await LevelApi.getUserLevel(UserStore.user.id);

      runInAction(() => {
        this.currentTier = data.current;
        this.progress = data.progress;
        this.accumulated = data.accumulated;
        this.nextTier = data.next;
      });
    }
  }
}

export default new LevelStore();
